import React from 'react';
import { motion } from 'framer-motion';
import { MonitorIcon, UsersIcon, SmartphoneIcon, FingerprintIcon, MessageSquareIcon, FileTextIcon, DatabaseIcon, BrainIcon, PuzzleIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { WebsiteMockup, PortalMockup, AppMockup } from './platform/ExperienceMockups';
import { platformIntro, experiences, foundation, platformClosing, type Bilingual, type ExperienceId } from '../data/platformContent';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: EASE
  }
};
const DOT_FIELD: React.CSSProperties = {
  backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px), radial-gradient(currentColor 1px, transparent 1px)',
  backgroundSize: '18px 18px, 26px 26px',
  backgroundPosition: '0 0, 9px 13px'
};
const EXPERIENCE_ICONS: Record<ExperienceId, typeof MonitorIcon> = {
  website: MonitorIcon,
  portal: UsersIcon,
  app: SmartphoneIcon
};
const PILLAR_ICONS = {
  identity: FingerprintIcon,
  message: MessageSquareIcon,
  requests: FileTextIcon,
  data: DatabaseIcon,
  brain: BrainIcon
};
export function PlatformExperiences() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (value: Bilingual) => isFR ? value.fr : value.en;
  const mockups: Record<ExperienceId, React.ReactNode> = {
    website: <WebsiteMockup isFR={isFR} data-id="element-5384" data-mp="2p0v7kowde1bw" />,
    portal: <PortalMockup isFR={isFR} data-id="element-5385" data-mp="2ab78zvfkpgrv" />,
    app: <AppMockup isFR={isFR} data-id="element-5386" data-mp="2ac8kg10i45a4" />
  };
  return <section id="platform" className="bg-white border-t border-[#E4E6EC] py-16 lg:py-20 px-4 sm:px-6" data-id="element-5387" data-mp="2a94b6p86ijcl">
      <div className="max-w-[1320px] mx-auto" data-id="element-5388" data-mp="21aixb7dnjsi5">
        {/* Header */}
        <motion.div {...reveal} className="max-w-3xl mx-auto text-center" data-id="element-5389" data-mp="21clucbd82lwd">
          <div className="flex items-center justify-center gap-3" data-id="element-5390" data-mp="268annwynf6k8">
            <span className="w-6 h-px bg-[#4F46E5]/40" aria-hidden="true" data-id="element-5391" data-mp="btx3j1u1fnx6" />
            <p className="font-sans text-xs font-semibold tracking-[0.22em] text-[#4F46E5] uppercase" data-id="element-5392" data-mp="72nfg5o50sne">
              {pick(platformIntro.eyebrow)}
            </p>
            <span className="w-6 h-px bg-[#4F46E5]/40" aria-hidden="true" data-id="element-5393" data-mp="243ti59jngv1" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.14] tracking-tight text-[#121212] mt-4" data-id="element-5394" data-mp="3u8xv1x7mshy6">
            <span className="block" data-id="element-5395" data-mp="urge49w59elp">{pick(platformIntro.headlineLead)}</span>
            <span className="block" data-id="element-5396" data-mp="pu7qvuw52kdl">
              {pick(platformIntro.headlineBefore)}
              <span className="text-[#4F46E5]" data-id="element-5397" data-mp="lqrw743mqu3j">
                {pick(platformIntro.headlineAccent)}
              </span>
            </span>
          </h2>
          <p className="font-sans text-base text-[#5B6478] leading-relaxed mt-4" data-id="element-5398" data-mp="gut2r23g2lw9">
            {pick(platformIntro.intro)}
          </p>
        </motion.div>

        {/* Three experiences — the website gets the wider frame */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)] gap-4 mt-10" data-id="element-5399" data-mp="1h16o5k9gnz6l">
          {experiences.map((experience, index) => {
          const Icon = EXPERIENCE_ICONS[experience.id];
          return <motion.article key={experience.id} {...reveal} transition={{
            duration: 0.6,
            delay: index * 0.07,
            ease: EASE
          }} className="rounded-2xl border border-[#E4E6EC] bg-white shadow-[0_1px_3px_rgba(18,18,18,0.05)] p-5 flex flex-col sm:flex-row gap-5" data-id="element-5400" data-mp="1q01tku4ol58u" data-mpi={index}>
                <div className="sm:w-[38%] sm:shrink-0 flex flex-col" data-id="element-5401" data-mp="29plnyzk0uaxc">
                  <span className="w-11 h-11 rounded-full bg-[#4F46E5]/[0.07] flex items-center justify-center" data-id="element-5402" data-mp="29n7tosx612qu">
                    <Icon className="w-[1.15rem] h-[1.15rem] text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-5403" data-mp="2dokzj309d5xr" />
                  </span>
                  <p className="font-sans text-[10px] font-semibold tracking-[0.13em] uppercase text-[#4F46E5] mt-4" data-id="element-5404" data-mp="3k7kqmvnndpw8">
                    {pick(experience.label)}
                  </p>
                  <h3 className="font-serif text-2xl text-[#121212] leading-[1.15] mt-2" data-id="element-5405" data-mp="3u61o8bh3d2r9">
                    {pick(experience.headline)}
                  </h3>
                  <p className="font-sans text-sm text-[#5B6478] leading-relaxed mt-3" data-id="element-5406" data-mp="3p802tjw70rcm">
                    {pick(experience.body)}
                  </p>
                  <div className="mt-auto pt-4" data-id="element-5407" data-mp="2wjb05gfhodyr">
                    <div className="border-t border-[#E4E6EC] pt-3" data-id="element-5408" data-mp="2wggg3q0e0v5v">
                      <p className="font-sans text-[11px] text-[#9AA0B0] leading-relaxed" data-id="element-5409" data-mp="3b87iq7f699dm">
                        {pick(experience.footer)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-w-0 self-center" data-id="element-5410" data-mp="2ios4roxyqapy">
                  {mockups[experience.id]}
                </div>
              </motion.article>;
        })}
        </div>

        {/* Foundation */}
        <motion.div {...reveal} transition={{
        duration: 0.6,
        delay: 0.08,
        ease: EASE
      }} className="relative overflow-hidden rounded-2xl bg-[#4F46E5]/[0.045] px-6 py-9 lg:px-10 mt-4" data-id="element-5411" data-mp="y0fyytvwkw4m">
          <span className="pointer-events-none absolute -right-20 -top-20 w-72 h-72 rounded-full text-[#4F46E5]/30 opacity-30" style={DOT_FIELD} aria-hidden="true" data-id="element-5412" data-mp="oa4wlbrvauzu" />

          <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,2.3fr)] gap-8 lg:gap-10" data-id="element-5413" data-mp="1gyafru39wbxk">
            <div data-id="element-5414" data-mp="12zj4sgjpj6dn">
              <p className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase" data-id="element-5415" data-mp="1325bkdyrxhut">
                {pick(foundation.eyebrow)}
              </p>
              <h3 className="font-serif text-2xl lg:text-[1.75rem] text-[#121212] leading-snug mt-3" data-id="element-5416" data-mp="3np8ksww5cy7x">
                {foundation.statement.map(line => <span key={line.en} className="block" data-id="element-5417" data-mp="1y8mpgmwvwlku">
                    {pick(line)}
                  </span>)}
              </h3>
              <p className="font-sans text-sm text-[#5B6478] leading-relaxed mt-4" data-id="element-5418" data-mp="1tacr63fv54v0">
                {pick(foundation.body)}
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-2 lg:divide-x lg:divide-[#4F46E5]/15 lg:border-l lg:border-[#4F46E5]/15 lg:pl-6" data-id="element-5419" data-mp="1ki12am0edph5">
              {foundation.pillars.map(pillar => {
              const Icon = PILLAR_ICONS[pillar.icon];
              return <div key={pillar.id} className="text-center lg:px-2" data-id="element-5420" data-mp="15jp76jsoaos3">
                    <span className="w-11 h-11 mx-auto rounded-full bg-white flex items-center justify-center" data-id="element-5421" data-mp="15m1xbk8l45g8">
                      <Icon className="w-[1.15rem] h-[1.15rem] text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-5422" data-mp="2zxzx6vl07ozg" />
                    </span>
                    <div className="font-sans text-[10px] font-semibold tracking-[0.11em] uppercase text-[#4F46E5] mt-3 leading-relaxed" data-id="element-5423" data-mp="ho41fg1mntr5">
                      {pick(pillar.title)}
                    </div>
                    <div className="mt-2 space-y-0.5" data-id="element-5424" data-mp="rg03f26u440n">
                      {pillar.lines.map(line => <p key={line.en} className="font-sans text-xs text-[#5B6478] leading-snug" data-id="element-5425" data-mp="vfs8v60xlu1s">
                          {pick(line)}
                        </p>)}
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* Expansion strip */}
          <div className="relative mt-8 rounded-xl bg-white/80 border border-[#4F46E5]/15 px-5 py-4 flex items-center gap-3" data-id="element-5426" data-mp="cnpslvs7cfs2">
            <PuzzleIcon className="w-5 h-5 text-[#4F46E5] shrink-0" strokeWidth={1.75} aria-hidden="true" data-id="element-5427" data-mp="1o4phsij96ehv" />
            <p className="font-sans text-sm text-[#121212]/75 leading-snug flex-1" data-id="element-5428" data-mp="zsnib6vslbty">
              {pick(foundation.expansion)}
            </p>
            <ArrowRightIcon className="w-4 h-4 text-[#4F46E5] shrink-0" strokeWidth={2} aria-hidden="true" data-id="element-5429" data-mp="18l8zwjnotch7" />
          </div>
        </motion.div>

        {/* Closing */}
        <motion.div {...reveal} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: EASE
      }} className="relative overflow-hidden mt-8 px-6 py-8 text-center" data-id="element-5430" data-mp="1ihu79bn6izo7">
          <span className="pointer-events-none absolute -left-24 -bottom-24 w-80 h-80 rounded-full text-[#4F46E5]/25 opacity-30" style={DOT_FIELD} aria-hidden="true" data-id="element-5431" data-mp="1rjv2y42rldnr" />
          <span className="pointer-events-none absolute -right-24 -bottom-24 w-80 h-80 rounded-full text-[#4F46E5]/25 opacity-30" style={DOT_FIELD} aria-hidden="true" data-id="element-5432" data-mp="3tbpx35sz8zi8" />
          <p className="relative font-serif text-2xl md:text-[2rem] leading-snug" data-id="element-5433" data-mp="3f5xzet2v81gs">
            <span className="block text-[#121212]" data-id="element-5434" data-mp="3j5q3ashbdjft">
              {pick(platformClosing.lead)}
            </span>
            <span className="block text-[#4F46E5]" data-id="element-5435" data-mp="35f3cdmhsoxua">
              {pick(platformClosing.accent)}
            </span>
          </p>
          <p className="relative font-sans text-[11px] tracking-[0.18em] uppercase text-[#9AA0B0] mt-4" data-id="element-5436" data-mp="3acd458snplla">
            {pick(platformClosing.caption)}
          </p>
        </motion.div>
      </div>
    </section>;
}